<template>
	<div>
		<div v-if="damageCase && payment && loadedPDF !== null">
			<div v-if="!showMailSend">
				<div class="text-left" v-if="damageCase && payment">
					<span class="font-bold">{{ formatMoney(damageCase.paymentDamageAssessorOffenerBetrag) }}</span> {{ $t('open_amount_due_since') }}
					<span class="font-bold">{{ openDays }}</span> {{ openDaysUnit }}
					<div class="mt-2">
						{{ $t('current_dunning_status') }} <span class="font-bold">{{ statusString }}</span>
					</div>
					<div class="mt-2" v-if="!isMahnverfahren">
						{{ $t('due_date_input') }} <span class="font-bold">{{ getStringFromDate(payment.deadline) }}</span>
					</div>
				</div>

				<div>
					<div>
						<div v-if="damageCase.paymentDamageAssessorOffenerBetrag">
							<div class="flex xl:flex-row flex-col">
								<div class="xl:w-1/2 main">
									<BaseSelect
										ref="BaseSelect"
										:label="$t('select_new_status')"
										v-model="payment.status"
										:options="statusOptions"
										:required="true"
										@input="updateText()"
									/>
									<BaseDatepicker
										ref="BaseDatepicker"
										:label="$t('due_date')"
										v-model="payment.deadline"
										:required="true"
										@input="updatePDF()"
										v-if="!isMahnverfahren && showTextarea"
									/>
									<BaseSelect
										ref="BaseSelect"
										:label="$t('lawyers_office')"
										:disable-search="false"
										v-model="payment.lawyerOffice"
										:options="lawyerOfficeOptions"
										options-label="name"
										:required="true"
										:disabled="!showTextarea"
										v-if="payment && payment.status && isMahnverfahren"
										@input="updatePDFnow"
									/>

									<BaseInput v-model="text" label="Text" ref="BaseInput" v-if="showTextarea">
										<textarea v-model="text" class="text break-normal" @input="updatePDF(true)" />
									</BaseInput>
								</div>
								<div class="xl:w-1/2 h-224">
									<div class="flex justify-center">
										<PDFThumbnailPreview
											ref="pdfPreview1"
											:pdf-u-r-l="pdfURL"
											:uuid="uuid"
											show-route-params="false"
											:type="payment.status.value"
											:autoreload="true"
											v-if="payment.status"
										/>
									</div>
								</div>
							</div>

							<BaseInput v-model="payment.note" label="Notizen" ref="BaseInput" class="mt-4">
								<textarea v-model="payment.note" @change="saveNote" />
							</BaseInput>

							<div class="mt-4">
								<div class="text-right">
									<BaseButton @click="printPdf" class="mr-4 btn-blue" v-if="showOnlyPrintButton" :disabled="btnDisabled">
										{{ $t('save_pdf_and_print') }}
									</BaseButton>
									<BaseButton @click="preSentEmail" class="btn-blue" v-if="showSentEmailButton" :disabled="btnDisabled">
										{{ getPdfButtonLabel }}
									</BaseButton>
								</div>
							</div>
						</div>
						<div v-else-if="payment.rechnungsbetrag">
							{{ $t('billing_amount_paid') }}
						</div>
					</div>
				</div>
			</div>
			<div v-else>
				<DunningMailLawyer
					@cancelMail="hideMail"
					@sendMail="saveAndSentEmail"
					v-model="mail"
					:pdf-u-r-l="pdfURL"
					:pdf-type="payment.status.value"
					:attachments="pdfAttachmentList"
					:receiver-type="receiverType"
					:uuid="uuid"
				></DunningMailLawyer>
			</div>
		</div>
		<div class="flex flex-grow justify-center w-full opacity-75" v-else>
			<LoadingSpinner />
		</div>
	</div>
</template>

<script>
import BaseInput from '@/components/forms/inputs/BaseInput';
import apiDefaults from '@/api/defaultConfiguration';
import LoadingSpinner from '@/components/navigation/LoadingSpinner';
import BaseSelect from '@/components/forms/inputs/BaseSelect';
import BaseDatepicker from '@/components/forms/inputs/BaseDatepicker';
import { DunningApiFactory, LawyerofficesApiFactory, PaymentApiFactory, PdfApiFactory } from '@/api/generated';
import BaseButton from '@/components/forms/buttons/BaseButton';
import defaultConfiguration from '@/api/defaultConfiguration';
import { addDaysToDate, getOptionFromOptionsArray, getUnixTimestamp, makeShadowCopy, getStringFromDate } from '@/plugins/Utils';
import Validate from '@/mixins/Validate';
import LoadingSpinnerMixin from '@/mixins/LoadingSpinner';
import Formatter from '@/mixins/Formatter';
import DunningStatus from '@/mixins/damagecase/DunningStatus';
import DunningMailLawyer from '@/components/damagecases/edit/DunningMailLawyer.vue';
import PDFThumbnailPreview from '@/components/damagecases/forms/PDFThumbnailPreview';
import { PDFDocumentTypes } from '@/api/models/PDFDocumentTypes';
import PDFRepository from '@/api/repositories/PDFRepository';

const paymentApiFactory = PaymentApiFactory(defaultConfiguration.config(), defaultConfiguration.basePath, defaultConfiguration.axios);
const pdfApiFactory = PdfApiFactory(defaultConfiguration.config(), defaultConfiguration.basePath, defaultConfiguration.axios);

function loadPDF() {
	if (this.payment.status) {
		new PDFRepository()
			.getPDFFile(this.$route.params.processId || this.$route.params.id, this.getMahnstatus)
			.then(response => {
				this.pdfURL = response.data.data.uuid ? apiDefaults.axios.defaults.baseURL + 'api/pdf/' + response.data.data.uuid + '/file' : '';
				this.loadedPDF = true;
			})
			.catch(e => {
				this.$store.dispatch('reportLogs');
				this.updateText();
				this.loadedPDF = false;
			});
	} else {
		this.loadedPDF = false;
	}
}

function getPaymentLawyers() {
	LawyerofficesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
		.lawyerofficesGet()
		.then(ressource => {
			this.lawyerOfficeOptions = ressource.data;
		});
}

function init() {
	this.$store.dispatch('forceUpdateDamageCase', { id: parseInt(this.$route.params.processId || this.$route.params.id) }).then(response => {
		this.payment = makeShadowCopy(this.damageCase.paymentDamageAssessor);

		if (this.payment) {
			this.payment.deadline = this.payment.deadline ? new Date(this.payment.deadline * 1000) : addDaysToDate(15, new Date());
			this.payment.status = getOptionFromOptionsArray(this.statusOptions, this.payment.status);
			this.payment.lawyerOffice = this.payment.lawyers_office ? this.payment.lawyers_office : null;
		}

		this.statusString = this.makeStatusLabel(this.payment?.status?.value);
		loadPDF.call(this);
	});
	getPaymentLawyers.call(this);
}

export default {
	name: 'Warning',
	components: { PDFThumbnailPreview, BaseButton, BaseDatepicker, BaseSelect, LoadingSpinner, BaseInput, DunningMailLawyer },
	mixins: [Validate, LoadingSpinnerMixin, Formatter, DunningStatus],
	data() {
		return {
			lawyerOfficeOptions: [],
			payment: {},
			pdfURL: '',
			timeouteId: null,
			text: null,
			loadedPDF: null,
			statusString: null,
			showMailSend: false,
			pdfAttachmentList: {},
			btnDisabled: true,
			mail: {
				subject: '',
				text: '',
				receiver: '',
			},
			uuid: '',
		};
	},
	created() {
		init.call(this);
	},
	methods: {
		getApiPayment() {
			return {
				...this.payment,
				text: this.text,
				lawyer_office: undefined,
				lawyerOffice: undefined,
				lawyers_office_id: this.payment.lawyerOffice?.id ? this.payment.lawyerOffice?.id : null,
				status: this.payment.status?.value,
				deadline: this.payment.deadline ? getUnixTimestamp(this.payment.deadline) : null,
			};
		},
		updateOptions(statusOptions) {
			for (const i in statusOptions) {
				if (statusOptions[i].value === 'payment_reminder_customer_without_lawyer' && this.payment.payment_reminder_customer_without_lawyer) {
					statusOptions[i].display += ' (' + this.getStringFromDate(this.payment.payment_reminder_customer_without_lawyer * 1000) + ')';
				} else if (
					statusOptions[i].value === 'second_payment_reminder_customer_without_lawyer' &&
					this.payment.second_payment_reminder_customer_without_lawyer
				) {
					statusOptions[i].display += ' (' + this.getStringFromDate(this.payment.second_payment_reminder_customer_without_lawyer * 1000) + ')';
				} else if (statusOptions[i].value === 'payment_reminder_customer_with_lawyer' && this.payment.payment_reminder_customer_with_lawyer) {
					statusOptions[i].display += ' (' + this.getStringFromDate(this.payment.payment_reminder_customer_with_lawyer * 1000) + ')';
				} else if (
					statusOptions[i].value === 'second_payment_reminder_customer_with_lawyer' &&
					this.payment.second_payment_reminder_customer_with_lawyer
				) {
					statusOptions[i].display += ' (' + this.getStringFromDate(this.payment.second_payment_reminder_customer_with_lawyer * 1000) + ')';
				} else if (statusOptions[i].value === 'last_dunning_notice' && this.payment.last_dunning_notice) {
					statusOptions[i].display += ' (' + this.getStringFromDate(this.payment.last_dunning_notice * 1000) + ')';
				} else if (statusOptions[i].value === 'first_dunning_notice' && this.payment.first_dunning_notice) {
					statusOptions[i].display += ' (' + this.getStringFromDate(this.payment.first_dunning_notice * 1000) + ')';
				}
			}
		},
		getStringFromDate(string) {
			return getStringFromDate(new Date(string));
		},
		saveAndSentEmail() {
			this.save(true);
		},
		sentEmail() {
			this.setLoadingStatus(true);
			DunningApiFactory(defaultConfiguration.config(), defaultConfiguration.basePath, defaultConfiguration.axios)
				.damageCasePaymentEmailDamageCaseIdPost(this.$route.params.processId || this.$route.params.id, {
					...this.mail,
					receiverType: this.receiverType,
				})
				.then(response => {
					this.setLoadingStatus(false);
					this.hideMail();
					this.$swal(this.$t('general.success').toString(), this.$t('email_sent').toString(), 'success');
				});
		},
		updateText() {
			if (this.isStatusClosed) {
				loadPDF.call(this);
			} else {
				this.btnDisabled = true;
				PaymentApiFactory(defaultConfiguration.config(), defaultConfiguration.basePath, defaultConfiguration.axios)
					.damageCasePaymentDunningtextDamageCaseIdTypeDeadlineGet(
						this.$route.params.processId || this.$route.params.id,
						this.payment.status.value,
						parseInt(this.payment.deadline.getTime() / 1000),
					)
					.then(response => {
						this.text = response.data.text;
						this.updatePDFnow();
					});
			}
		},
		updatePDF(textedit) {
			this.btnDisabled = true;
			if (!textedit) this.updateText();
			clearTimeout(this.timeouteId);
			this.timeouteId = setTimeout(() => this.updatePDFnow(), 1000);
		},
		updatePDFnow() {
			this.btnDisabled = true;
			this.pdfURL = null;
			PdfApiFactory(defaultConfiguration.config(), defaultConfiguration.basePath, defaultConfiguration.axios)
				.pdfDamagecasesPaymentTypeTempPost(this.payment.status.value, { ...this.damageCase, payment: this.getApiPayment() })
				.then(response => {
					this.uuid = response.data.uuid;
					this.pdfURL = this.uuid
						? apiDefaults.axios.defaults.baseURL + 'api/pdf/damagecases/' + this.uuid + '/temp/file#toolbar=0&navpanes=0&scrollbar=0'
						: null;
					this.btnDisabled = false;
				});
			return true;
		},
		saveNote() {
			paymentApiFactory
				.damageCasePaymentDamageCasePaymentIdPut(this.payment.id, { note: this.payment.note })
				.then(response => {
					this.$swal(this.$t('general.success').toString(), this.$t('note_saved').toString(), 'success');
				})
				.catch(error => {
					this.$store.dispatch('reportLogs');
					this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
				});
		},
		save(sentEmail) {
			if (!this.validate()) {
				this.$swal(this.$t('general.error').toString(), this.$t('please_check_input').toString(), 'error');
				return false;
			}

			paymentApiFactory
				.damageCasePaymentDamageCasePaymentIdPut(this.payment.id, this.getApiPayment())
				.then(response => {
					this.createPDF(sentEmail);
				})
				.catch(error => {
					this.$store.dispatch('reportLogs');
					this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
				});
		},
		createPDF(supressMessage) {
			if (!this.validate()) {
				this.$swal(this.$t('general.error').toString(), this.$t('please_check_input').toString(), 'error');
				return false;
			}

			pdfApiFactory
				.pdfDamagecasesPaymentTypeSavePost(this.payment.status.value, { ...this.damageCase, payment: this.getApiPayment() })
				.then(response => {
					this.$store.commit('setConfirmLeaving', false);
					if (!supressMessage) {
						this.$swal(this.$t('general.success').toString(), this.$t('pdf_generated').toString(), 'success')
							.then(() => {
								init.call(this);
								this.$refs['pdfPreview1'].openPDF();
							})
							.catch(error => {
								this.$store.dispatch('reportLogs');
								this.$swal(this.$t('general.error').toString(), this.$t('pdf_generation_error').toString(), 'error');
							});
					} else {
						this.sentEmail();
						init.call(this);
					}
				})
				.catch(error => {
					this.$store.dispatch('reportLogs');
					this.$swal(this.$t('general.error').toString(), this.$t('pdf_generation_error').toString(), 'error');
				});
		},
		formatedMoney(money) {
			if (!money) {
				return 0;
			}
			return new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, style: 'currency', currency: 'EUR' }).format(money);
		},
		translateStatus(status) {
			switch (status) {
				case 'payment_reminder_customer_without_lawyer':
				case 'payment_reminder_customer_with_lawyer':
					return this.$t('payment_reminder_first');
				case 'second_payment_reminder_customer_without_lawyer':
				case 'second_payment_reminder_customer_with_lawyer':
					return this.$t('payment_reminder_second');
				case 'last_dunning_notice':
					return this.$t('dunning_notice_last');
				case 'first_dunning_notice':
					return this.$t('dunning_notice');
			}
			return status;
		},
		printPdf() {
			this.save(false);
		},
		preSentEmail() {
			const status = this.payment.status?.value;

			if (!this.validate()) {
				this.$swal(this.$t('general.error').toString(), this.$t('please_check_input').toString(), 'error');
				return false;
			}

			if (!this.damageCase.customer.email && (status === 'zahlungserinnerung_kunde' || status === 'letzte_mahnung')) {
				this.save(false);
			} else {
				PaymentApiFactory(defaultConfiguration.config(), defaultConfiguration.basePath, defaultConfiguration.axios)
					.damageCasePaymentDunningtextDamageCaseIdTypeDeadlineGet(
						this.$route.params.processId || this.$route.params.id,
						'lawyerMailText|' + this.payment.status.value,
						parseInt(this.payment.deadline.getTime() / 1000),
					)
					.then(response => {
						this.mail.subject = this.getSubject();
						this.mail.text = response.data.text;
						this.mail.receiver = this.getRecieverEmail;
						this.mail.receiverType = this.$t('victim').toString();
						this.pdfAttachmentList = Object.assign({}, response.data.pdfs);
						this.showMailSend = true;
					});
			}
		},
		getSubject() {
			let customerName = '';
			if (this.damageCase.customer.gender === 'company') {
				customerName = this.damageCase.customer.companyName;
			} else {
				customerName = this.damageCase.customer.firstName + ' ' + this.damageCase.customer.lastName;
			}
			let sub = customerName + ' - ' + this.translateStatus(this.payment.status.value);
			if (this.isMahnverfahren) {
				sub =
					this.$t('case_wages_claim') +
					(this.damageCase.customer.gender === 'company' ? this.damageCase.customer.companyName : this.damageCase.customer.lastName);
			}
			return sub;
		},
		hideMail() {
			this.showMailSend = false;
		},
	},
	computed: {
		showOnlyPrintButton() {
			return this.payment.status && this.showTextarea && this.damageCase.customer.email;
		},
		getPdfButtonLabel() {
			if (this.damageCase.customer.email) {
				return this.$t('generate_pdf_send_email_customer');
			}
			return this.$t('save_pdf_and_print');
		},
		getRecieverEmail() {
			const status = this.payment.status?.value;
			if (status === 'zahlungsanweisung_anwalt') {
				return this.damageCase.lawyerOffice?.email;
			} else if (status === 'im_mahnverfahren') {
				return this.payment.lawyerOffice?.email;
			}

			return this.damageCase.customer?.email;
		},
		receiverType() {
			return this.$t('customer');
		},
		isMahnverfahren() {
			return this.payment.status?.value === 'im_mahnverfahren';
		},
		openDays() {
			if (this.payment.open_days === 1) {
				return this.$t('yesterday');
			} else if (this.payment.open_days === 0) {
				return this.$t('today');
			} else {
				return this.payment.open_days;
			}
		},
		openDaysUnit() {
			if (this.payment.open_days === 0 || this.payment.open_days === 1) {
				return '';
			} else {
				return this.$t('days');
			}
		},
		showSentEmailButton() {
			return this.payment.status && this.showTextarea;
		},
		isStatus() {
			return this.payment?.status !== undefined;
		},
		isStatusButNoPDFLoaded() {
			return this.isStatus && !this.loadedPDF;
		},
		isStatusClosed() {
			return this.payment.abgeschlossen != null;
		},
		showTextarea() {
			const isSameStatus = this.payment?.status?.value === this.payment?._shadow?.status;
			return (this.isStatus && !isSameStatus && !this.isStatusClosed) || this.isStatusButNoPDFLoaded;
		},
		getMahnstatus() {
			switch (this.payment.status?.value) {
				case 'payment_reminder_customer_without_lawyer':
					return PDFDocumentTypes.PaymentReminderWithoutLawyer;
				case 'second_payment_reminder_customer_without_lawyer':
					return PDFDocumentTypes.SecondPaymentReminderWithoutLawyer;
				case 'payment_reminder_customer_with_lawyer':
					return PDFDocumentTypes.PaymentReminderWithLawyer;
				case 'second_payment_reminder_customer_with_lawyer':
					return PDFDocumentTypes.SecondPaymentReminderWithLawyer;
				case 'first_dunning_notice':
					return PDFDocumentTypes.DunningFirst;
				case 'last_dunning_notice':
					return PDFDocumentTypes.DunningLast;
			}
			return PDFDocumentTypes.PaymentReminderWithLawyer;
		},
		statusOptions() {
			const array = [
				{
					value: 'payment_reminder_customer_without_lawyer',
					display: this.$t('payment_reminder_customer_without_lawyer'),
				},
				{
					value: 'second_payment_reminder_customer_without_lawyer',
					display: this.$t('second_payment_reminder_customer_without_lawyer'),
				},
				{
					value: 'payment_reminder_customer_with_lawyer',
					display: this.$t('payment_reminder_customer_with_lawyer'),
				},
				{
					value: 'second_payment_reminder_customer_with_lawyer',
					display: this.$t('second_payment_reminder_customer_with_lawyer'),
				},
				{
					value: 'first_dunning_notice',
					display: this.$t('first_dunning_notice'),
				},
				{
					value: 'last_dunning_notice',
					display: this.$t('last_dunning_notice'),
				},
			];

			this.updateOptions(array);

			return array;
		},
		damageCase() {
			return { ...this.$store.getters.getDamageCase };
		},
	},
};
</script>

<style scoped>
.text {
	height: 25rem;
	resize: none;
}
</style>
